import { useEffect, useState } from 'react';
import { api, ErroApi } from '../../api';
import { useAuth } from '../../auth';
import {
  Campo, Carregando, Erro, Modal, Selo, Tabela, Vazio, mascaraCelular, quando,
} from '../../componentes/ui';
import type { Empresa, Escritorio, UsuarioAdmin } from '../../tipos';

export function Pessoas() {
  const { usuario: eu } = useAuth();
  const [lista, setLista] = useState<UsuarioAdmin[] | null>(null);
  const [escritorios, setEscritorios] = useState<Escritorio[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [erro, setErro] = useState('');
  const [nova, setNova] = useState(false);

  async function carregar() {
    try {
      const { usuarios } = await api.usuarios();
      setLista(usuarios);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as pessoas.');
    }
  }

  useEffect(() => {
    void carregar();
    api.escritorios().then((r) => setEscritorios(r.escritorios)).catch(() => {});
    api.empresas().then((r) => setEmpresas(r.empresas)).catch(() => {});
  }, []);

  async function alternar(u: UsuarioAdmin) {
    try {
      await api.alterarUsuario(u.id, { ativo: !u.ativo });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui alterar.');
    }
  }

  const soltas = (lista || []).filter(
    (u) => u.ativo && !u.isAdmin && !u.ehSofthouse && !u.escritorios.length && !u.empresas.length,
  );

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Pessoas</h1>
          <p>
            Quem entra no portal. A identidade é o celular — não há senha para criar, esquecer ou
            compartilhar. Criar a pessoa não abre nada: é o vínculo que dá acesso.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setNova(true)}>Dar acesso a alguém</button>
      </header>

      <Erro>{erro}</Erro>

      {soltas.length > 0 && (
        <p className="nota nota-atencao">
          {soltas.length} pessoa(s) sem nenhum vínculo. Elas conseguem entrar e não veem nada —
          falta ligar a um escritório ou a uma empresa.
        </p>
      )}

      {!lista ? <Carregando /> : lista.length === 0 ? (
        <Vazio
          titulo="Ninguém com acesso ainda"
          descricao="Comece pelo responsável do escritório: é ele quem passa a enxergar as empresas."
          acao={<button className="btn btn-primary btn-sm" onClick={() => setNova(true)}>Dar o primeiro acesso</button>}
        />
      ) : (
        <Tabela
          itens={lista}
          chaveDe={(u) => u.id}
          classeLinha={(u) => (u.ativo ? undefined : 'linha-inativa')}
          colunas={[
            {
              rotulo: 'Nome',
              principal: true,
              celula: (u) => (
                <>
                  <strong>{u.nome}</strong>
                  {u.isAdmin && <span className="selo info" style={{ marginLeft: 8 }}>admin</span>}
                  {u.ehSofthouse && <span className="selo neutro" style={{ marginLeft: 6 }}>suporte</span>}
                  {Number(u.id) === eu?.id && <span className="selo neutro" style={{ marginLeft: 6 }}>você</span>}
                </>
              ),
            },
            { rotulo: 'Celular', fraco: true, mono: true, celula: (u) => u.celular },
            {
              rotulo: 'Enxerga',
              fraco: true,
              celula: (u) => (
                u.escritorios.length === 0 && u.empresas.length === 0
                  ? (u.isAdmin ? 'tudo (admin)' : <span className="td-alerta">nada ainda</span>)
                  : [
                    ...u.escritorios.map((e) => `${e.nome}${e.papel === 'responsavel' ? ' (resp.)' : ''}`),
                    ...u.empresas.map((e) => e.razaoSocial),
                  ].join(' · ')
              ),
            },
            { rotulo: 'Último acesso', fraco: true, celula: (u) => quando(u.ultimoAcessoEm) },
            {
              rotulo: 'Ações',
              acoes: true,
              celula: (u) => (
                <>
                  {!u.ativo && <Selo tipo="inativo" />}
                  {Number(u.id) !== eu?.id && (
                    <button className="link-botao" onClick={() => alternar(u)}>
                      {u.ativo ? 'Tirar acesso' : 'Devolver acesso'}
                    </button>
                  )}
                </>
              ),
            },
          ]}
        />
      )}

      {nova && (
        <NovaPessoa
          escritorios={escritorios}
          empresas={empresas}
          onFechar={() => setNova(false)}
          onCriada={() => { setNova(false); void carregar(); }}
        />
      )}
    </div>
  );
}

function NovaPessoa({
  escritorios, empresas, onFechar, onCriada,
}: {
  escritorios: Escritorio[];
  empresas: Empresa[];
  onFechar: () => void;
  onCriada: () => void;
}) {
  const [nome, setNome] = useState('');
  const [celular, setCelular] = useState('');
  const [tipo, setTipo] = useState<'contador' | 'cliente' | 'admin'>('contador');
  const [escritorioId, setEscritorioId] = useState<number | ''>('');
  const [empresaId, setEmpresaId] = useState<number | ''>('');
  const [responsavel, setResponsavel] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const faltaVinculo = (tipo === 'contador' && !escritorioId) || (tipo === 'cliente' && !empresaId);

  async function salvar() {
    setErro('');
    setSalvando(true);
    try {
      await api.criarUsuario({
        nome,
        celular,
        isAdmin: tipo === 'admin',
        escritorioId: tipo === 'contador' ? Number(escritorioId) : undefined,
        empresaId: tipo === 'cliente' ? Number(empresaId) : undefined,
        papel: responsavel ? 'responsavel' : 'contador',
      });
      onCriada();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      titulo="Dar acesso"
      descricao="A pessoa entra digitando este celular e o código que chega no WhatsApp dela."
      onFechar={onFechar}
    >
      <Erro>{erro}</Erro>

      <Campo rotulo="Nome">
        <input className="input" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
      </Campo>

      <Campo rotulo="Celular" ajuda="É a identidade dela aqui dentro. Precisa ser um número com WhatsApp.">
        <div className="phone-input">
          <span className="cc">+55</span>
          <input type="tel" inputMode="numeric" placeholder="(81) 99999-9999"
            value={celular} onChange={(e) => setCelular(mascaraCelular(e.target.value))} />
        </div>
      </Campo>

      <Campo rotulo="O que ela vai enxergar">
        <div className="opcoes">
          {([
            ['contador', 'Um escritório', 'Enxerga todas as empresas daquele escritório.'],
            ['cliente', 'Uma empresa', 'Enxerga só os documentos daquela empresa.'],
            ['admin', 'Tudo (administrador)', 'Gerencia o Conti9 inteiro. Use com parcimônia.'],
          ] as const).map(([valor, titulo, texto]) => (
            <button
              key={valor}
              type="button"
              className={`opcao ${tipo === valor ? 'ativa' : ''}`}
              onClick={() => setTipo(valor)}
            >
              <strong>{titulo}</strong>
              <span>{texto}</span>
            </button>
          ))}
        </div>
      </Campo>

      {tipo === 'contador' && (
        <>
          <Campo rotulo="Escritório">
            <select className="input" value={escritorioId}
              onChange={(e) => setEscritorioId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Escolha…</option>
              {escritorios.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </Campo>
          <label className="check">
            <input type="checkbox" checked={responsavel} onChange={(e) => setResponsavel(e.target.checked)} />
            <span>É o responsável pelo escritório (poderá dar acesso a outras pessoas)</span>
          </label>
        </>
      )}

      {tipo === 'cliente' && (
        <Campo rotulo="Empresa">
          <select className="input" value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Escolha…</option>
            {empresas.map((e) => <option key={e.id} value={e.id}>{e.razaoSocial}</option>)}
          </select>
        </Campo>
      )}

      <div className="modal-acoes">
        <button className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button className="btn btn-primary" onClick={salvar}
          disabled={salvando || nome.trim().length < 2 || celular.length < 14 || faltaVinculo}>
          {salvando ? <span className="spinner" /> : 'Dar acesso'}
        </button>
      </div>
    </Modal>
  );
}
